/**
 * Cash Tracker screen preferences — column widths + which sections to show/print.
 * Stored in localStorage (per browser/device). Never touches ledger data or KPIs.
 */

export const CASH_TX_COLUMN_KEYS = [
  'date',
  'direction',
  'entity',
  'counterparty',
  'category',
  'amount',
  'status',
  'note',
  'actions',
];

/** Default pixel widths aligned with the previous fixed colgroup. */
export const DEFAULT_CASH_TX_COLUMN_WIDTHS = {
  date: 184,
  direction: 96,
  entity: 124,
  counterparty: 128,
  category: 156,
  amount: 124,
  status: 148,
  note: 220,
  actions: 92,
};

export const CASH_TX_COLUMN_MIN_WIDTH = {
  date: 88,
  direction: 56,
  entity: 72,
  counterparty: 80,
  category: 88,
  amount: 80,
  status: 72,
  note: 96,
  actions: 64,
};

export const CASH_TX_COLUMN_MAX_WIDTH = {
  date: 320,
  direction: 160,
  entity: 280,
  counterparty: 360,
  category: 280,
  amount: 220,
  status: 200,
  note: 480,
  actions: 140,
};

export const CASH_VIEW_SECTION_DEFS = [
  { key: 'summary', label: 'Summary KPIs', primary: false },
  { key: 'balances', label: 'Opening / account balances', primary: false },
  { key: 'entities', label: 'Entities', primary: false },
  { key: 'transactions', label: 'Transactions', primary: true },
  { key: 'reconciliation', label: 'Budget / reconciliation', primary: false },
];

export const DEFAULT_CASH_VIEW_SECTIONS = {
  summary: true,
  balances: true,
  entities: true,
  transactions: true,
  reconciliation: true,
};

export const CASH_TX_WIDTHS_STORAGE_KEY = 'cashTracker.txColumnWidths.v1';
export const CASH_VIEW_SECTIONS_STORAGE_KEY = 'cashTracker.viewSections.v1';

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

export function clampCashTxColumnWidth(key, width) {
  const min = CASH_TX_COLUMN_MIN_WIDTH[key] ?? 64;
  const max = CASH_TX_COLUMN_MAX_WIDTH[key] ?? 480;
  const n = Number(width);
  if (!Number.isFinite(n)) return DEFAULT_CASH_TX_COLUMN_WIDTHS[key] ?? min;
  return clamp(Math.round(n), min, max);
}

export function normalizeCashTxColumnWidths(raw) {
  const next = { ...DEFAULT_CASH_TX_COLUMN_WIDTHS };
  if (!raw || typeof raw !== 'object') return next;
  CASH_TX_COLUMN_KEYS.forEach((key) => {
    if (raw[key] != null) next[key] = clampCashTxColumnWidth(key, raw[key]);
  });
  return next;
}

export function totalCashTxTableWidth(widths) {
  const w = normalizeCashTxColumnWidths(widths);
  return CASH_TX_COLUMN_KEYS.reduce((sum, key) => sum + w[key], 0);
}

export function normalizeCashViewSections(raw) {
  const next = { ...DEFAULT_CASH_VIEW_SECTIONS };
  if (!raw || typeof raw !== 'object') return next;
  CASH_VIEW_SECTION_DEFS.forEach(({ key }) => {
    if (typeof raw[key] === 'boolean') next[key] = raw[key];
  });
  // Transactions are the primary brief — keep them on if everything else is off.
  if (!Object.values(next).some(Boolean)) next.transactions = true;
  return next;
}

export function readLocalJson(key, storage = globalThis.localStorage) {
  try {
    if (!storage) return null;
    const raw = storage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function writeLocalJson(key, value, storage = globalThis.localStorage) {
  try {
    if (!storage) return false;
    storage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

export function loadCashTxColumnWidths(storage = globalThis.localStorage) {
  return normalizeCashTxColumnWidths(readLocalJson(CASH_TX_WIDTHS_STORAGE_KEY, storage));
}

export function saveCashTxColumnWidths(widths, storage = globalThis.localStorage) {
  const normalized = normalizeCashTxColumnWidths(widths);
  writeLocalJson(CASH_TX_WIDTHS_STORAGE_KEY, normalized, storage);
  return normalized;
}

export function resetCashTxColumnWidths(storage = globalThis.localStorage) {
  try {
    storage?.removeItem?.(CASH_TX_WIDTHS_STORAGE_KEY);
  } catch { /* ignore */ }
  return { ...DEFAULT_CASH_TX_COLUMN_WIDTHS };
}

export function loadCashViewSections(storage = globalThis.localStorage) {
  return normalizeCashViewSections(readLocalJson(CASH_VIEW_SECTIONS_STORAGE_KEY, storage));
}

export function saveCashViewSections(sections, storage = globalThis.localStorage) {
  const normalized = normalizeCashViewSections(sections);
  writeLocalJson(CASH_VIEW_SECTIONS_STORAGE_KEY, normalized, storage);
  return normalized;
}

/**
 * Sections that appear in the on-screen tracker and the print/export extract.
 * `balances` and `entities` are independent: opening/reserves vs legal-vehicle identity.
 */
export function resolveCashExportSections(sections) {
  const s = normalizeCashViewSections(sections);
  return {
    summary: !!s.summary,
    balances: !!s.balances,
    entities: !!s.entities,
    transactions: !!s.transactions,
    reconciliation: !!s.reconciliation,
  };
}

/**
 * Snapshot of what Print / Save PDF (and the on-screen export preview) will include.
 * Column widths match the live transaction table.
 */
export function getCashPrintSelection(sections, columnWidths) {
  return {
    sections: resolveCashExportSections(sections),
    columnWidths: normalizeCashTxColumnWidths(columnWidths),
    tableWidth: totalCashTxTableWidth(columnWidths),
  };
}
